import { BaseService } from './BaseService';
import {
  FarmSettingsViewModel,
  AnimalCapacityViewModel,
  BarChartData,
  TranslationViewModel,
  AppEventLog,
  AppEventLogApplicationType,
  FarmSettingsOptions,
  WebNotificationViewModel
} from '../models/settings';
import { FarmContactViewModel } from '../models/user';
import { PageableResult } from '../models/common';

export class SettingsService extends BaseService {
  getFarmSettings = async (): Promise<FarmSettingsViewModel> => {
    const result = await this.Fetch('/settings/farm', 'GET');

    return this.FromResult<FarmSettingsViewModel>(result);
  };

  updateFarmSettings = async (
    barChartData?: BarChartData[],
    contacts?: FarmContactViewModel[],
    options?: FarmSettingsOptions
  ): Promise<string> => {
    const command = JSON.stringify({ barChartData, contacts, options });

    const result = await this.Fetch('/settings/farm', 'POST', command);

    return this.FromResult<string>(result);
  };

  getAnimalCapacity = async (from?: string, to?: string): Promise<AnimalCapacityViewModel> => {
    const query = this.BuildQueryString({ From: from, To: to });

    const result = await this.Fetch(`/settings/animalcapacity${query ? `?${query}` : ''}`, 'GET');

    return this.FromResult<AnimalCapacityViewModel>(result);
  };

  getWebNotifications = async (page: number, size: number): Promise<PageableResult<WebNotificationViewModel>> => {
    const query = this.BuildQueryString({ page, size });

    const result = await this.Fetch(`/notifications?${query}`, 'GET');

    return this.FromResult<PageableResult<WebNotificationViewModel>>(result);
  };

  viewNotifications = async (ids: string[]): Promise<string[]> => {
    const result = await this.Fetch('/notifications/view', 'POST', JSON.stringify({ ids }));

    return this.FromResult<string[]>(result);
  };

  getTranslations = async (): Promise<TranslationViewModel[]> => {
    const result = await this.Fetch(`/locales`, 'GET');

    return this.FromResult<TranslationViewModel[]>(result);
  };

  getTranslationByIsoCode = async (iso: string): Promise<string> => {
    const result = await this.Fetch(`/locales/${iso}`, 'GET');

    return this.FromResult<string>(result);
  };

  updateTranslation = async (id: string, language: string, translations: string): Promise<string> => {
    const command = JSON.stringify({ id, language, translations });

    const result = await this.Fetch('/locales', 'PUT', command);

    return this.FromResult<string>(result);
  };

  createTranslation = async (language: string, isoCode: string): Promise<string> => {
    const command = JSON.stringify({ language, isoCode });

    const result = await this.Fetch('/locales', 'POST', command);

    return this.FromResult<string>(result);
  };

  getFarmLogs = async (
    page: number,
    size: number,
    type: AppEventLogApplicationType | null,
    errorOnly: boolean,
    farmId?: string
  ): Promise<PageableResult<AppEventLog>> => {
    const query = this.BuildQueryString({ page, size, type, errorOnly, farmId });

    const result = await this.Fetch(`/eventlog?${query}`, 'GET');

    return this.FromResult<PageableResult<AppEventLog>>(result);
  };

  createEventLog = async (log: any): Promise<string> => {
    const result = await this.Fetch('/eventlog', 'POST', JSON.stringify(log));

    return this.FromResult<string>(result);
  };

  getConfigurationProviders = async (farmId: string): Promise<ConfigurationProviderViewModel[]> => {
    const result = await this.Fetch(`/farms/${farmId}/configuration-providers`, 'GET');
    return this.FromResult<ConfigurationProviderViewModel[]>(result);
  };

  upsertConfigurationProvider = async (farmId: string, type: string, settings: any): Promise<void> => {
    const result = await this.Fetch(
      `/farms/${farmId}/configuration-providers/${type}`,
      'PUT',
      JSON.stringify({ settings })
    );
    if (result.status === 204 || result.ok) return;
    const json = await result.json();
    if (json.message) throw new Error(json.message);
    else if (json.errors) throw json.errors;
    else throw json;
  };

  enableConfigurationProvider = async (farmId: string, type: string): Promise<void> => {
    const result = await this.Fetch(`/farms/${farmId}/configuration-providers/${type}/enable`, 'POST');
    if (result.status === 204 || result.ok) return;
    const json = await result.json();
    if (json.message) throw new Error(json.message);
    else if (json.errors) throw json.errors;
    else throw json;
  };

  disableConfigurationProvider = async (farmId: string, type: string): Promise<void> => {
    const result = await this.Fetch(`/farms/${farmId}/configuration-providers/${type}/disable`, 'POST');
    if (result.status === 204 || result.ok) return;
    const json = await result.json();
    if (json.message) throw new Error(json.message);
    else if (json.errors) throw json.errors;
    else throw json;
  };
}

export interface ConfigurationProviderViewModel {
  id: string;
  providerType: string;
  isEnabled: boolean;
  settings: any;
  createdAt: string;
  updatedAt: string;
}

export default SettingsService;
